import {SchoolPersonnelInvitationModelV1} from 'resources/GeneratedModels/SchoolPersonnelInvitation/SchoolPersonnelInvitationModel.v1';
import {invalidatePartialInterest} from 'resources/mandark.resource';
import {addToast} from '@albert-io/atomic';

export async function sendEmailInvites({newEmails, schoolId, rejectedTeachers}) {
  const rejectedTeacherEmails = rejectedTeachers.getSchoolPersonnel().map((teacher) => {
    if (!teacher.has('teacher') || !teacher.getTeacher().size === 0) {
      return teacher.getTeacher().getEmail();
    }
    return null;
  });
  // These are the users that are already in the "Rejected" column, so they don't need an email invite.
  // Instead, their status will be set to approved.
  const toApprove = rejectedTeachers.getSchoolPersonnel().filter((entry) => {
    if (!entry.has('teacher') || !entry.getTeacher().size === 0) {
      return newEmails.includes(entry.getTeacher().getEmail());
    }
    return null;
  });
  const toApprovePromises = toApprove.map((teacher) => {
    return teacher.setStatus('approved').save();
  });

  // These are users who have never been associated with this school in Albert, so they will be getting an invite for the first time.
  const toSendInvites = newEmails.filter((email) => !rejectedTeacherEmails.includes(email));
  const schoolPersonnelInvitations = toSendInvites.map((email) => {
    return SchoolPersonnelInvitationModelV1.getDefaultModel()
      .setEmail(email)
      .setAcceptedAt(null)
      .setSentEmailAt(null)
      .updateRelationship('school_v5', schoolId);
  });
  const toSendInvitesPromises = schoolPersonnelInvitations.map((invite) => {
    return invite.save();
  });
  const results = await Promise.allSettled([...toApprovePromises, ...toSendInvitesPromises]);
  const rejectedItems = results.filter((result) => result.status === 'rejected');
  invalidatePartialInterest({resourcePath: ['schools_v5', schoolId, 'school_personnel_v1']});
  invalidatePartialInterest({resourcePath: ['schools_v5', schoolId]});

  if (rejectedItems.length) {
    addToast({
      message: 'There was a problem updating your teacher list.',
      title: 'Error',
      type: 'danger'
    });
  } else {
    addToast({
      message: 'Your teacher list was updated!',
      title: 'Success',
      type: 'success'
    });
  }
}

export async function refreshTeacherList({
  newEmails,
  schoolId,
  missingEmails,
  schoolTeachers,
  pendingStaff,
  rejectedTeachers
}) {
  // These are the users that are already in the "Rejected" column, so they don't need an email invite.
  // Instead, their status will be set to approved.
  const filteredRejectedTeachers = rejectedTeachers
    .getSchoolPersonnel()
    .filter((entry) => entry.getTeacher().size !== 0);
  const toApprove = filteredRejectedTeachers.filter((entry) =>
    newEmails.includes(entry.getTeacher().getEmail())
  );
  const toApprovePromises = toApprove.map((teacher) => {
    return teacher.setStatus('approved').save();
  });

  // These are users who have never been associated with this school in Albert, so they will be getting an invite for the first time.
  const rejectedTeacherEmails = filteredRejectedTeachers.map((teacher) => {
    return teacher.getTeacher().getEmail();
  });
  const toSendInvites = newEmails.filter((email) => !rejectedTeacherEmails.includes(email));
  const schoolPersonnelInvitations = toSendInvites.map((email) => {
    return SchoolPersonnelInvitationModelV1.getDefaultModel()
      .setEmail(email)
      .setAcceptedAt(null)
      .setSentEmailAt(null)
      .updateRelationship('school_v5', schoolId);
  });
  const toSendInvitesPromises = schoolPersonnelInvitations.map((invite) => {
    return invite.save();
  });
  const results = await Promise.allSettled([...toApprovePromises, ...toSendInvitesPromises]);
  const rejectedItems = results.filter((result) => result.status === 'rejected');

  // Rejecting teacher emails that are found in school and no longer needed
  const filteredSchoolTeachers = schoolTeachers
    .getSchoolPersonnel()
    .filter((entry) => entry.getTeacher().size !== 0);
  const missingTeacherEmails = filteredSchoolTeachers.filter((entry) =>
    missingEmails.includes(entry.getTeacher().getEmail())
  );
  const rejectedEmailPromises = missingTeacherEmails.map((record) => {
    const newRecord = record.setStatus('rejected');
    return newRecord.save();
  });
  const rejectedEmailResults = await Promise.allSettled(rejectedEmailPromises);
  const rejectedEmailItems = rejectedEmailResults.filter((result) => result.status === 'rejected');

  // Reject users accounts who go from teacher -> student
  const newStudentUsers = schoolTeachers
    .getSchoolPersonnel()
    .filter((entry) => entry.getTeacher().size === 0);
  const rejectedStudentPromises = newStudentUsers.map((record) => {
    const newRecord = record.setStatus('rejected');
    return newRecord.save();
  });
  const rejectedStudentResults = await Promise.allSettled(rejectedStudentPromises);
  const rejectedStudentItems = rejectedStudentResults.filter(
    (result) => result.status === 'rejected'
  );

  // Deleting invites that were never accepted
  const pendingInvites = pendingStaff
    .getSchoolPersonnelInvitations()
    .filter((entry) => missingEmails.includes(entry.getEmail()));
  const deleteInvitePromises = pendingInvites.map((invite) => {
    return invite.delete();
  });
  const deleteInviteResults = await Promise.allSettled(deleteInvitePromises);
  const rejectedDeleteInviteItems = deleteInviteResults.filter(
    (result) => result.status === 'rejected'
  );
  invalidatePartialInterest({resourcePath: ['schools_v5', schoolId, 'school_personnel_v1']});
  invalidatePartialInterest({resourcePath: ['schools_v5', schoolId]});

  if (
    rejectedStudentItems.length ||
    rejectedItems.length ||
    rejectedEmailItems.length ||
    rejectedDeleteInviteItems.length
  ) {
    addToast({
      message: 'There was a problem updating your teacher list.',
      title: 'Error',
      type: 'danger'
    });
  } else {
    addToast({
      message: 'Your teacher list was updated!',
      title: 'Success',
      type: 'success'
    });
  }
}
